"use client";

import React, { forwardRef } from "react";
import * as Primitive from "@radix-ui/react-toast";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { Icon } from "../../atoms/Icon/Icon";
import { cn } from "../../lib/cn";

export const ToastProvider = Primitive.Provider;

export const ToastViewport = forwardRef<
  React.ElementRef<typeof Primitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof Primitive.Viewport>
>(({ className = "", ...props }, ref) => (
  <Primitive.Viewport
    ref={ref}
    className={cn("fixed bottom-0 right-0 z-50 flex w-full max-w-sm flex-col gap-2 p-4", className)}
    {...props}
  />
));
ToastViewport.displayName = "ToastViewport";

const toast = cva("relative flex items-start gap-3 rounded-md border bg-surface-raised p-4 shadow-lg", {
  variants: {
    status: {
      neutral: "border-border",
      success: "border-success-border",
      warning: "border-warning-border",
      error: "border-error-border",
    },
  },
  defaultVariants: { status: "neutral" },
});

type ToastProps = React.ComponentPropsWithoutRef<typeof Primitive.Root> &
  VariantProps<typeof toast> & {
    title: React.ReactNode;
    description?: React.ReactNode;
  };

export const Toast = forwardRef<React.ElementRef<typeof Primitive.Root>, ToastProps>(
  ({ status, title, description, className = "", ...props }, ref) => (
    <Primitive.Root ref={ref} className={cn(toast({ status }), className)} {...props}>
      <div className="flex-1">
        <Primitive.Title className="text-sm font-semibold text-text">{title}</Primitive.Title>
        {description && (
          <Primitive.Description className="mt-1 text-sm text-text-muted">{description}</Primitive.Description>
        )}
      </div>
      <Primitive.Close aria-label="Dismiss" className="text-text-subtle hover:text-text">
        <Icon icon={X} size="sm" />
      </Primitive.Close>
    </Primitive.Root>
  )
);
Toast.displayName = "Toast";
